import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useContent } from '../state/ContentContext';
import { useAuth } from '../state/auth';
import { useToast } from '../state/ToastContext';
import { useDialog } from './Dialogs';
import { Product, Story } from '../domain/types';
import { catalogService } from '../domain/services/catalogService';
import { logger } from '../infrastructure/logger';
import { UI_ANIMATIONS, DISPLAY_LIMITS } from '../domain/constants';

// --- STORY VIEWER MODAL (Isolated for performance) ---
const StoryViewerModal = ({
    stories,
    activeIndex,
    onClose,
    onNext,
    onPrev,
    onDelete,
    onGoToProduct,
    isAdminView
}: {
    stories: Story[],
    activeIndex: number,
    onClose: () => void,
    onNext: () => void,
    onPrev: () => void,
    onDelete: (id: string) => void,
    onGoToProduct: (id: string) => void,
    isAdminView: boolean
}) => {
    const [progress, setProgress] = useState(0);
    const progressInterval = useRef<number | null>(null);
    const currentStory = stories[activeIndex];

    useEffect(() => {
        setProgress(0);
        startTimer();
        return () => stopTimer();
    }, [activeIndex]);

    const startTimer = () => {
        if (progressInterval.current) clearInterval(progressInterval.current);
        const duration = currentStory?.duration || UI_ANIMATIONS.STORY_DEFAULT_DURATION_MS;
        const intervalTime = UI_ANIMATIONS.STORY_PROGRESS_UPDATE_INTERVAL_MS; 
        const step = 100 / (duration / intervalTime);

        progressInterval.current = window.setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    onNext();
                    return 0;
                }
                return prev + step;
            });
        }, intervalTime);
    };

    const stopTimer = () => {
        if (progressInterval.current) clearInterval(progressInterval.current);
    };

    const timelineSteps = useMemo(() => stories.map((s, i) => (
        <div key={s.id} className="h-1 bg-white/30 rounded-full flex-1 overflow-hidden backdrop-blur-md">
            <div
                className={`h-full bg-white transition-all duration-100 ease-linear ${i < activeIndex ? 'w-full' : i === activeIndex ? '' : 'w-0'}`}
                style={{ width: i === activeIndex ? `${progress}%` : undefined }}
            ></div>
        </div>
    )), [stories, activeIndex, progress]);

    if (!currentStory) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-black flex flex-col animate-fade-in touch-none">
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute inset-0 bg-cover bg-center blur-3xl scale-150 opacity-60" style={{ backgroundImage: `url("${currentStory.mediaUrl}")` }}></div>
                <div className="absolute inset-0 bg-background-dark/80 backdrop-blur-sm"></div>
            </div>

            <div className="relative z-10 w-full h-full flex flex-col">
                <div className="absolute top-0 left-0 w-full px-2 pt-safe mt-2 z-30 flex gap-1">
                    {timelineSteps}
                </div>

                <div className="absolute top-0 left-0 w-full pt-safe mt-6 px-4 z-30 flex justify-between items-center text-white drop-shadow-md">
                    <div className="flex items-center gap-3">
                        <img src={currentStory.brandAvatar} className="w-9 h-9 rounded-full border border-white/50 bg-black" alt={`Avatar de ${currentStory.brandName || 'la tienda'}`} />
                        <div className="flex flex-col">
                            <span className="font-bold text-sm leading-tight">{currentStory.brandName}</span>
                            <span className="text-white/70 text-[10px] font-medium">{currentStory.isAd ? '• Publicidad' : '• Autor'}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {isAdminView && (
                            <button onClick={(e) => { e.stopPropagation(); onDelete(currentStory.id); }} className="p-2 bg-red-500/20 hover:bg-red-500/40 text-red-500 rounded-full backdrop-blur-md transition-colors"><span className="material-symbols-outlined text-2xl">delete</span></button>
                        )}
                        <button onClick={onClose} className="p-2 bg-black/20 hover:bg-black/40 rounded-full backdrop-blur-md transition-colors"><span className="material-symbols-outlined text-2xl">close</span></button>
                    </div>
                </div>

                <div className="flex-1 flex items-center justify-center relative w-full h-full p-0 md:p-4">
                    <div className="absolute inset-0 flex z-20">
                        <div className="w-1/3 h-full cursor-pointer" onClick={onPrev}></div>
                        <div className="w-2/3 h-full cursor-pointer" onClick={onNext}></div>
                    </div>

                    <div className="relative w-full h-full md:max-w-[400px] md:h-auto md:aspect-[9/16] md:rounded-2xl overflow-hidden shadow-2xl bg-black">
                        {currentStory.type === 'video' ? (
                            <video src={currentStory.mediaUrl} className="w-full h-full object-cover" autoPlay muted playsInline loop />
                        ) : (
                            <img src={currentStory.mediaUrl} className="w-full h-full object-cover" alt="Story" />
                        )}
                        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/90 to-transparent pointer-events-none"></div>
                    </div>

                    {currentStory.productId && (
                        <div className="absolute bottom-0 w-full md:max-w-md px-6 pb-safe mb-4 z-30 flex flex-col items-center gap-3 animate-slide-up pointer-events-none">
                            <div className="pointer-events-auto w-full flex flex-col items-center gap-3">
                                <div className="bg-black/60 backdrop-blur-xl px-4 py-2 rounded-full text-white text-[10px] text-center border border-white/10 shadow-lg flex items-center gap-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                    <span><strong>{currentStory.brandName}</strong> recomienda este producto</span>
                                </div>
                                <button onClick={(e) => { e.stopPropagation(); onGoToProduct(currentStory.productId); }} className="w-full bg-white text-black font-bold py-3.5 rounded-full shadow-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform hover:bg-gray-100 group">
                                    <span>Ver Producto</span>
                                    <span className="material-symbols-outlined text-lg group-hover:translate-x-1 transition-transform">arrow_forward</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- MAIN RAIL COMPONENT ---
export const StoriesRail = ({ shopId, currentUserId }: { shopId?: string; currentUserId?: string }) => {
  const navigate = useNavigate();
  const { activeStories, markStoryViewed, createStory, deleteStory, getActiveStoriesByShop } = useContent();
  const { user, isAdmin, isSuperAdmin, viewAsUser } = useAuth();
  const { addToast } = useToast();
  const { showConfirm, DialogComponents } = useDialog();

    // Viewer State
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    // Filter stories by shop if shopId is provided
    const filteredStories = useMemo(() => {
        if (shopId && getActiveStoriesByShop) {
            return getActiveStoriesByShop(shopId);
        }
        return activeStories;
    }, [shopId, activeStories, getActiveStoriesByShop]);

    // Creator State
    const [showCreator, setShowCreator] = useState(false);
    const [showProductSearch, setShowProductSearch] = useState(false);
    const [showDiscardAlert, setShowDiscardAlert] = useState(false);
    const [storyDuration, setStoryDuration] = useState<number>(UI_ANIMATIONS.STORY_DURATION_SHORT);
    const storyLifetime = UI_ANIMATIONS.STORY_LIFETIME_HOURS;
    const [creatorStep, setCreatorStep] = useState(1);
    const [newStoryMedia, setNewStoryMedia] = useState<string | null>(null);
    const [newStoryType, setNewStoryType] = useState<'image' | 'video'>('image');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    const [products, setProducts] = useState<Product[]>([]);
    const [productSearch, setProductSearch] = useState('');

    useEffect(() => {
        if (showCreator) catalogService.getAllProductsForDisplay(100).then(products => setProducts(products));
    }, [showCreator]);

    const handleNext = () => {
        if (activeIndex === null) return;
        const currentStory = filteredStories[activeIndex];
        if (activeIndex < filteredStories.length - 1) {
            if (currentStory && !currentStory.isViewed) {
                markStoryViewed(currentStory.id);
            }
            setActiveIndex(activeIndex + 1);
        } else {
            if (currentStory && !currentStory.isViewed) {
                markStoryViewed(currentStory.id);
            }
            setActiveIndex(null);
        }
    };

    const handlePrev = () => {
        if (activeIndex !== null && activeIndex > 0) setActiveIndex(activeIndex - 1);
    };

  const handleDelete = async (id: string) => {
    const confirmed = await showConfirm({
      title: 'Eliminar Historia',
      message: '¿Eliminar esta historia?',
      type: 'danger'
    });
    if (confirmed) {
      const currentCount = filteredStories.length;
      deleteStory(id)
        .then(() => {
          addToast('Historia eliminada', 'info');
          if (currentCount === 1) setActiveIndex(null);
          else if (activeIndex === currentCount - 1) handlePrev();
        })
        .catch(err => {
          logger.error('Failed to delete story', err);
          addToast('Error al eliminar historia', 'error');
        });
    }
  };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setNewStoryMedia(URL.createObjectURL(file));
            setNewStoryType(file.type.startsWith('video') ? 'video' : 'image');
            setCreatorStep(2);
        }
    };

    const handlePostStory = async () => {
        if (!newStoryMedia || !user) return;
        const success = await createStory({ mediaUrl: newStoryMedia, type: newStoryType, productId: selectedProduct?.id || '', shopId, duration: storyDuration, isAd: false }, storyLifetime, { name: user.name, avatar: user.avatar });
        if (success) { addToast(`Historia subida`, 'success'); resetCreator(); }
    };

    const resetCreator = () => {
        setShowCreator(false);
        setCreatorStep(1);
        setNewStoryMedia(null);
        setSelectedProduct(null);
        setShowDiscardAlert(false);
    };

    if (filteredStories.length === 0 && !isAdmin && !isSuperAdmin) return null;

    const canCreateStory = (isAdmin || isSuperAdmin) && !viewAsUser && shopId !== undefined;

    return (
        <>
            <div className="flex gap-4 overflow-x-auto no-scrollbar px-4 pt-4 pb-2">
                {canCreateStory && (
                    <div onClick={() => setShowCreator(true)} className="flex flex-col items-center gap-1.5 cursor-pointer shrink-0">
                        <div className="w-[68px] h-[68px] rounded-full p-[2px] border-2 border-dashed border-gray-300 dark:border-white/20 flex items-center justify-center relative hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                            <div className="w-full h-full bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center"><span className="material-symbols-outlined text-primary text-2xl">add</span></div>
                            <div className="absolute bottom-0 right-0 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center border-2 border-white dark:border-background-dark"><span className="material-symbols-outlined text-[12px] font-bold">add</span></div>
                        </div>
                        <span className="text-[10px] text-text-secondary dark:text-gray-400 font-medium">Tu Historia</span>
                    </div>
                )}

                {filteredStories.map((story, index) => (
                    <div key={story.id} className="flex flex-col items-center gap-1.5 cursor-pointer shrink-0 group" onClick={() => setActiveIndex(index)}>
                        <div className={`w-[68px] h-[68px] rounded-full p-[3px] ${story.isViewed ? 'bg-gray-300 dark:bg-white/20' : 'bg-gradient-to-tr from-yellow-400 via-primary to-purple-600'} transition-all group-hover:scale-105`}>
                            <div className="w-full h-full rounded-full border-2 border-white dark:border-background-dark overflow-hidden bg-gray-200">
                                <img src={story.brandAvatar} alt={story.brandName} className="w-full h-full object-cover" />
                            </div>
                        </div>
                        <span className="text-[10px] text-text-main dark:text-white font-medium truncate w-16 text-center">{story.brandName}</span>
                    </div>
                ))}
            </div>

            {activeIndex !== null && createPortal(
                <StoryViewerModal
                    stories={filteredStories}
                    activeIndex={activeIndex}
                    onClose={() => setActiveIndex(null)}
                    onNext={handleNext}
                    onPrev={handlePrev}
                    onDelete={handleDelete}
                    onGoToProduct={(id) => { setActiveIndex(null); navigate(`/product/${id}`); }}
                    isAdminView={(isAdmin || isSuperAdmin) && !viewAsUser}
                />, 
                document.body
            )}

            {/* Creator Portal Simplified for brevity, remains similar logic but can be further isolated if needed */}
            {showCreator && createPortal(
                <div className="fixed inset-0 z-[100] flex justify-center bg-black/90 backdrop-blur-sm animate-fade-in">
                    <div className="w-full h-full md:max-w-xl lg:max-w-2xl bg-black relative flex flex-col shadow-2xl overflow-hidden">
                        {showDiscardAlert && (
                            <div className="absolute inset-0 z-[110] bg-black/80 flex items-center justify-center p-6 animate-fade-in">
                                <div className="bg-white dark:bg-surface-dark rounded-2xl p-6 w-full max-w-xs shadow-2xl text-center">
                                    <h3 className="text-lg font-bold text-black dark:text-white mb-2">¿Descartar historia?</h3>
                                    <div className="flex gap-3 mt-6"><button onClick={() => setShowDiscardAlert(false)} className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-white/10 text-black dark:text-white font-bold text-sm">Volver</button><button onClick={resetCreator} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm">Descartar</button></div>
                                </div>
                            </div>
                        )}
                        {creatorStep === 1 && (
                            <div className="flex-1 flex flex-col justify-center items-center gap-12 p-8">
                                <button onClick={() => setShowCreator(false)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-md"><span className="material-symbols-outlined">close</span></button>
                                <h2 className="text-white text-2xl font-black text-center uppercase tracking-wide">Crear Historia</h2>
                                <div className="grid grid-cols-2 gap-6 w-full max-w-sm">
                                    <label className="aspect-square bg-gray-900 rounded-3xl border-2 border-gray-700 flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-95 transition-transform hover:border-purple-500"><span className="material-symbols-outlined text-5xl text-purple-500">photo_camera</span><span className="text-white font-bold">Cámara</span><input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} /></label>
                                    <label className="aspect-square bg-gray-900 rounded-3xl border-2 border-gray-700 flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-95 transition-transform hover:border-blue-500"><span className="material-symbols-outlined text-5xl text-blue-500">photo_library</span><span className="text-white font-bold">Galería</span><input type="file" accept="image/*,video/*" className="hidden" onChange={handleFileSelect} /></label>
                                </div>
                            </div>
                        )}
                        {creatorStep === 2 && newStoryMedia && (
                            <div className="flex-1 flex flex-col relative bg-gray-900 h-full">
                                <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-black">
                                    {newStoryType === 'video' ? <video src={newStoryMedia} className="w-full h-full object-cover" autoPlay muted loop playsInline /> : <img src={newStoryMedia} className="w-full h-full object-cover" alt="Preview" />}
                                    <div className="absolute top-0 left-0 w-full p-4 pt-safe flex justify-between items-start z-20">
                                        <button onClick={() => setShowDiscardAlert(true)} className="w-10 h-10 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-md"><span className="material-symbols-outlined">close</span></button>
                                        <div className="flex flex-col gap-3 items-end">
                                            <div className="relative">
                                                <button onClick={() => setShowProductSearch(!showProductSearch)} className={`w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md ${selectedProduct ? 'bg-green-500' : 'bg-black/40'} text-white`}><span className="material-symbols-outlined">shopping_bag</span></button>
                                                {showProductSearch && (
                                                    <div className="absolute top-12 right-0 w-72 bg-white dark:bg-gray-800 rounded-xl p-3 shadow-2xl animate-fade-in border border-black/10 z-50">
                                                        <input autoFocus type="text" placeholder="Buscar..." className="w-full bg-gray-100 dark:bg-white/10 rounded-lg px-3 py-2 text-sm mb-2 outline-none" value={productSearch} onChange={e => setProductSearch(e.target.value)} />
                                                        <div className="max-h-48 overflow-y-auto no-scrollbar space-y-1">
                                                            {products.filter(p => p.title.toLowerCase().includes(productSearch.toLowerCase())).slice(0, DISPLAY_LIMITS.STORY_SEARCH_RESULTS).map(p => (
                                                                <div key={p.id} onClick={() => { setSelectedProduct(p); setShowProductSearch(false); }} className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg cursor-pointer flex items-center gap-3 border-b border-gray-100 dark:border-white/5 last:border-0"><img src={p.imageUrl} className="w-8 h-8 rounded object-cover" alt={`Imagen de ${p.title}`} /><span className="text-xs font-bold truncate flex-1">{p.title}</span></div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <button onClick={() => setStoryDuration(prev => prev === UI_ANIMATIONS.STORY_DURATION_SHORT ? UI_ANIMATIONS.STORY_DURATION_LONG : UI_ANIMATIONS.STORY_DURATION_SHORT)} className="w-10 h-10 rounded-full bg-black/40 text-white flex items-center justify-center backdrop-blur-md font-bold text-[10px] flex-col leading-none shadow-sm"><span className="material-symbols-outlined text-[16px]">timer</span><span className="mt-0.5">{storyDuration / 1000}s</span></button>
                                        </div>
                                    </div>
                                    {selectedProduct && <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white text-black px-4 py-2 rounded-xl shadow-xl flex items-center gap-2 animate-slide-up z-20 max-w-[90%]"><span className="material-symbols-outlined">link</span><span className="font-bold text-sm truncate">{selectedProduct.title}</span><button onClick={() => setSelectedProduct(null)} className="ml-2 text-gray-500"><span className="material-symbols-outlined text-sm">close</span></button></div>}
                                </div>
                                <div className="p-4 pb-safe bg-black/90 backdrop-blur-lg flex items-center justify-between gap-4 border-t border-white/10 shrink-0 z-30">
                                    <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-gray-700 bg-cover bg-center border border-white/20" style={{backgroundImage: `url("${user?.avatar}")`}}></div><div className="flex flex-col"><span className="text-white text-sm font-bold">Tu Historia</span><span className="text-white/50 text-[10px]">Visible por {storyLifetime}hs</span></div></div>
                                    <button onClick={handlePostStory} className="bg-white text-black px-6 py-3 rounded-full font-bold flex items-center gap-2 hover:bg-gray-200 shadow-lg">Compartir <span className="material-symbols-outlined filled text-primary">send</span></button>
                                </div>
                            </div>
                        )}
      </div>
    </div>,
    document.body
  )}
  {DialogComponents}
</>
  );
};